import { useRef, useState } from "react";
import { T } from "react-translator-component";
import { useAppDispatch } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { setErrorObj } from "../../../redux/errorReducer";
import {
  Box, Button, ClickAwayListener, Dialog, DialogContent, DialogTitle, Grow,
  MenuItem, MenuList, Paper, Popper,
  Typography
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import cancel from "../../../assets/images/cancel.png";
import checkicon from "../../../assets/images/checkicon.png";
import deleteicon from "../../../assets/images/deleteicon.png";
import { mngStyles } from "../../../constants/styles";
import { colors } from "../../../../common/colors";
import ReportReasonModal from "../ReportReasonModal";
import apiServices from "../../../services/apiServices";
import NoticeBanner from "../../NoticeBanner";
import { isAdmin } from "../../../util/util";
import SubmitButton from "../../../../components/SubmitButton";

interface Props {
  post: any;
  highlightWords: string[];
  onChange(): void;
}

export default function HeaderPending({ post, highlightWords, onChange }: Props) {
  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [showReasons, setShowReasons] = useState<boolean>(false);
  const [displayNotice, setDisplayNotice] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showApprovalOption, setShowApprovalOption] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"approve" | "reject" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const approvalAnchorRef = useRef<any>(null)
  const sortMenuOptions = [
    {value: "approve", name: `${T("mng_pending_posts_approve")}`},
    {value: "reject", name: `${T("mng_pending_posts_reject")}`},
  ]

  const openConfirmationModal = (option: "approve" | "reject") => {
    setModalType(option)
    setShowModal(true)
  }

  const handleSubmit = async (showModalType : "approve" | "reject" | null) => {
    if (showModalType === null) return false

    setIsSubmitting(true)
    try {
      showModalType === "approve" ?
        await apiServices.approvePost(post.id) :
        await apiServices.rejectPost(post.id)

      onChange()
      const desc = showModalType === "approve" ?
        `${T("mng_pending_posts_approve_success")}` :
        `${T("mng_pending_posts_reject_success")}`
      setSuccessMessage(desc);
      setDisplayNotice(true)
      setShowModal(false)
      return true
    } catch (error: any) {
      console.error(error);
      dispatch(setErrorObj(error))
      return false
    } finally {
      setIsSubmitting(false)
    }
  };

  const handleDialogClose = () => {
    if (isSubmitting) return
    setShowModal(false)
  }


  if (!post) return (<></>)
  return (
    <>
      <Box sx={{ display: "flex" }}>
        {post.is_reported && (
          <Button
            onClick={() => setShowReasons(true)}
            color={"secondary"}
            variant="text"
          >
            {T("mng_rejected_posts_report_reasons")}
          </Button>
        )}
      </Box>

      <ReportReasonModal
        open={showReasons}
        post={post}
        highlightWords={highlightWords}
        onClose={() => setShowReasons(false)}
      />

      {isAdmin(admin) && (
        <Box
          style={style.menuIcon}
          ref={approvalAnchorRef}
          onClick={() => setShowApprovalOption(true)}
        >
          <MoreHorizIcon style={style.moreHorizIcon} />
        </Box>
      )}

      <Popper
        open={showApprovalOption}
        anchorEl={approvalAnchorRef.current}
        transition
      >
        {({TransitionProps}) => (
          <Grow {...TransitionProps} style={{transformOrigin:'top'}}>
            <Paper>
              <ClickAwayListener onClickAway={() => setShowApprovalOption(false)}>
                <MenuList>
                  {sortMenuOptions.map((option: any) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => openConfirmationModal(option.value)}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Dialog
        open={showModal}
        disableRestoreFocus={true}
        onClose={() => handleDialogClose()}
      >
        <img
          src={cancel}
          alt="BigCo Inc. logo"
          onClick={() => handleDialogClose()}
          style={imgStyle.cancelIconButton}
        />
        {modalType === "approve" && (
          <img
            src={checkicon}
            alt="BigCo Inc. logo"
            style={imgStyle.dialogIcon}
          />
        )}
        {modalType === "reject" && (
          <img
            src={deleteicon}
            alt="BigCo Inc. logo"
            style={imgStyle.dialogIcon}
          />
        )}
        <Typography
          sx={style.dialogConfirmationTitle}
        >
          {T("mng_dialog_confirmation_title")}
        </Typography>
        <DialogTitle
          style={{
            ...mngStyles.adminUserDialogTitle,
            color: modalType === "approve" ? colors.farm_green500 :
                   modalType === "reject" ? colors.farm_red600 : "",
            textAlign: "center",
          }}
        >
          {modalType === "approve" && T("mng_confirm_approve")}
          {modalType === "reject" && T("mng_confirm_reject")}
        </DialogTitle>
        <DialogContent>
          <div style={{ ...mngStyles.adminUserDialogContent, marginTop: 0 }}>
            <Button
              onClick={() => handleDialogClose()}
              variant="contained"
              sx={style.cancelButton}
              disabled={isSubmitting}
            >
              {T("mng_cancel")}
            </Button>
            {modalType === "approve" && (
              <SubmitButton
                onSubmit={() => handleSubmit(modalType)}
                variant="contained"
                color={"success"}
                sx={{ textAlign: "center", alignItems: "center" }}
                validate={async () => true}
                disabled={isSubmitting}
              >
                {T("mng_approve")}
              </SubmitButton>
            )}
            {modalType === "reject" && (
              <SubmitButton
                onSubmit={() => handleSubmit(modalType)}
                variant="contained"
                color={"error"}
                sx={{ textAlign: "center" }}
                validate={async () => true}
                disabled={isSubmitting}
              >
                {T("mng_reject")}
              </SubmitButton>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <NoticeBanner
        open={displayNotice}
        messageList={[successMessage]}
        type={"success"}
        style={style.banner}
        onClose={() => setDisplayNotice(false)}
      />
    </>
  );
}

const style = {
  menuIcon: {
    marginLeft: "10px",
    border: "1px solid #4792E0",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 30,
    height: 30,
  },

  moreHorizIcon: {
    color: "#4792E0",
    cursor: "pointer",
    fontSize: "25px",
    width: "30px"
  },

  banner: {
    width: "95%",
  },

  cancelButton: {
    textAlign: "center",
    backgroundColor: "gray",
    marginRight: 5,
  },

  dialogConfirmationTitle: {
    textAlign: "center",
    fontSize: 30,
    marginTop: 2,
    marginBottom: 2,
    height: 20,
  },

}

const imgStyle = {
  cancelIconButton: {
    width: 30,
    height: 30,
    alignSelf: "flex-end",
    marginRight: 10,
    marginTop: 10,
  },
  dialogIcon: {
    width: 100,
    height: 100,
    alignSelf: "center",
  }
}
